import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Shield,
  Crosshair,
  Clock,
  Wrench,
  Users,
  Award,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import ProductCard from "@/components/ProductCard";
import Seo from "@/components/Seo";
import ContactForm from "@/components/ContactForm";
import axios from "axios";

const API = "/api";

const features = [
  {
    icon: Shield,
    title: "Quality Assured",
    description:
      "Every part is precision-manufactured using high-quality materials to meet or exceed original specifications.",
  },
  {
    icon: Crosshair,
    title: "Historical Accuracy",
    description:
      "Meticulous attention to detail ensures authentic reproductions that honor the original designs.",
  },
  {
    icon: Clock,
    title: "Preserving History",
    description:
      "Keeping WWI, WWII, and Cold War-era firearms operational for generations to come.",
  },
];

const stats = [
  { value: "20+", label: "Years Experience" },
  { value: "100+", label: "Parts Reproduced" },
  { value: "2024", label: "Founded" },
];

export default function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        const response = await axios.get(`${API}/products?featured=true`);
        setFeaturedProducts(response.data.slice(0, 3));
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchFeaturedProducts();
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      <Seo path="/" />
      {/* Hero Section - Bento Grid Style */}
      <section className="relative min-h-screen pt-24 pb-16 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 bg-cover bg-center bg-grayscale opacity-20"
            style={{
              backgroundImage:
                "url('https://images.unsplash.com/photo-1595590424283-b8f17842773f?q=80&w=2070&auto=format&fit=crop')",
            }}
          />
          <div className="hero-gradient absolute inset-0" />
        </div>

        <div className="container-tactical relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-[80vh] items-center">
            {/* Main Hero Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-7 space-y-8"
            >
              <div className="space-y-4">
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="font-mono text-[#C0A062] text-sm tracking-[0.3em] uppercase"
                >
                  Precision Reproductions Since 2024
                </motion.p>
                <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-white uppercase tracking-tighter leading-[0.9]">
                  <span className="block">Rebuild.</span>
                  <span className="block">Restore.</span>
                  <span className="block text-[#C0A062]">Resurrect.</span>
                </h1>
              </div>

              <p className="text-lg sm:text-xl text-[#A1A1AA] max-w-xl leading-relaxed">
                Specializing in reproduction parts for vintage military
                firearms. Keeping historic weapons alive and functional for
                generations to come.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/shop" data-testid="hero-shop-button">
                  <Button className="btn-primary">
                    <span>Shop Parts</span>
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/about" data-testid="hero-about-button">
                  <Button className="btn-secondary">Learn More</Button>
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-8 pt-8 border-t border-white/10">
                {stats.map((stat, i) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + i * 0.1 }}
                  >
                    <p className="font-heading font-black text-3xl sm:text-4xl text-white">
                      {stat.value}
                    </p>
                    <p className="font-mono text-xs text-[#A1A1AA] uppercase tracking-wider">
                      {stat.label}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Hero Image Bento */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="lg:col-span-5 hidden lg:block"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="aspect-square rounded-sm overflow-hidden bg-[#121212] border border-white/5">
                    <img
                      src="https://images.squarespace-cdn.com/content/v1/68885ac792afd429e43ec94a/d9b294e3-ebe5-4d15-a837-d5042338a9b2/20251231_215241.jpg"
                      alt="MG08 Firing Pin"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="aspect-[4/3] rounded-sm overflow-hidden bg-[#C0A062]/10 border border-[#C0A062]/20 p-6 flex flex-col justify-end">
                    <Wrench className="w-8 h-8 text-[#C0A062] mb-2" />
                    <p className="font-heading font-bold text-white uppercase text-sm">
                      Custom Orders
                    </p>
                    <p className="text-[#A1A1AA] text-xs">Available</p>
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="aspect-[4/3] rounded-sm overflow-hidden bg-[#121212] border border-white/5">
                    <img
                      src="https://images.squarespace-cdn.com/content/v1/68885ac792afd429e43ec94a/2c288386-f3c9-417a-b4a2-4b75a36022a8/20250530_194328.jpg"
                      alt="PKM Trunnion"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="aspect-square rounded-sm overflow-hidden bg-[#121212] border border-white/5">
                    <img
                      src="https://images.squarespace-cdn.com/content/v1/68885ac792afd429e43ec94a/3f0c10c5-9450-435d-bc7d-9408036b1a05/20251231_215809.jpg"
                      alt="MP-35 Firing Pin"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 rounded-full border-2 border-white/20 flex justify-center pt-2">
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="w-1 h-2 bg-[#C0A062] rounded-full"
            />
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="section-spacing bg-[#0a0a0a]" data-testid="features-section">
        <div className="container-tactical">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="font-mono text-[#C0A062] text-sm tracking-[0.3em] uppercase mb-4">
              Why Choose Us
            </p>
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-white uppercase tracking-tight">
              Precision & Heritage
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-8 bg-[#121212] border border-white/5 hover:border-[#C0A062]/30 transition-colors duration-300"
                data-testid={`feature-${i}`}
              >
                <div className="feature-icon mb-6">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="font-heading font-bold text-white text-xl uppercase tracking-tight mb-3">
                  {feature.title}
                </h3>
                <p className="text-[#A1A1AA] leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="section-spacing bg-[#0f0f0f]" data-testid="featured-products-section">
        <div className="container-tactical">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12">
            <div>
              <p className="font-mono text-[#C0A062] text-sm tracking-[0.3em] uppercase mb-2">
                Our Catalog
              </p>
              <h2 className="font-heading font-bold text-4xl md:text-5xl text-white uppercase tracking-tight">
                Featured Parts
              </h2>
            </div>
            <Link to="/shop" data-testid="view-all-products">
              <Button className="btn-secondary">
                <span>View All</span>
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="aspect-square bg-[#121212] animate-pulse rounded-sm"
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredProducts.map((product, i) => (
                <ProductCard key={product.id} product={product} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* About Preview Section */}
      <section className="section-spacing bg-[#0a0a0a]" data-testid="about-preview-section">
        <div className="container-tactical">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <p className="font-mono text-[#C0A062] text-sm tracking-[0.3em] uppercase">
                Inside Our World
              </p>
              <h2 className="font-heading font-bold text-4xl md:text-5xl text-white uppercase tracking-tight">
                Keeping History Alive
              </h2>
              <p className="text-[#A1A1AA] text-lg leading-relaxed">
                At the heart of our design and manufacturing journey is a desire
                to provide collectors and builders alike the components to
                complete their unique projects, but also to provide collectors
                of rare and unique historical military weapons the means to keep
                their weapons running for generations to come.
              </p>
              <p className="text-[#A1A1AA] leading-relaxed">
                As the earliest NFA machine guns on the registry near 130 years
                old, we feel an obligation to treat those weapons with the
                respect and reverence they deserve, and to do whatever we can to
                make sure they stay functionally preserved well into the future.
              </p>
              <Link to="/about" data-testid="learn-more-about">
                <Button className="btn-secondary mt-4">
                  <span>Learn More</span>
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-sm overflow-hidden bg-[#121212] border border-white/5">
                <img
                  src="https://images.squarespace-cdn.com/content/v1/68885ac792afd429e43ec94a/14376194-00c3-4fde-9d26-b4b84365d99c/RGP+Logo.png"
                  alt="Resurrected Gun Parts Logo"
                  className="w-full h-full object-contain p-8"
                />
              </div>
              {/* Decorative Element */}
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#C0A062]/10 rounded-sm border border-[#C0A062]/20 -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Partner Program CTA */}
      <section className="section-spacing bg-[#121212]" data-testid="partner-cta-section">
        <div className="container-tactical">
          <div className="relative overflow-hidden rounded-sm border border-white/5 p-8 md:p-16">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(45deg, #C0A062 0, #C0A062 1px, transparent 0, transparent 50%)",
                  backgroundSize: "20px 20px",
                }}
              />
            </div>

            <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8">
              <div className="text-center lg:text-left">
                <div className="flex items-center justify-center lg:justify-start gap-3 mb-4">
                  <Users className="w-8 h-8 text-[#C0A062]" />
                  <Award className="w-8 h-8 text-[#C0A062]" />
                </div>
                <h2 className="font-heading font-bold text-3xl md:text-4xl text-white uppercase tracking-tight mb-4">
                  Join Our Partner Program
                </h2>
                <p className="text-[#A1A1AA] max-w-xl">
                  Have rare or unique historical firearm parts in your
                  collection? Put them to work for you. Loan us parts for
                  reproduction and receive free parts in return.
                </p>
              </div>
              <Link to="/partner-program" data-testid="partner-program-cta">
                <Button className="btn-primary whitespace-nowrap">
                  <span>Learn More</span>
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-spacing bg-[#0a0a0a]" data-testid="contact-section">
        <div className="container-tactical">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <p className="font-mono text-[#C0A062] text-sm tracking-[0.3em] uppercase">
                Get In Touch
              </p>
              <h2 className="font-heading font-bold text-4xl md:text-5xl text-white uppercase tracking-tight">
                Contact Us
              </h2>
              <p className="text-[#A1A1AA] text-lg leading-relaxed">
                Interested in working together? Fill out some info and we will
                be in touch shortly. We can't wait to hear from you!
              </p>

              <div className="space-y-4 pt-4">
                <div className="p-4 bg-[#121212] border border-white/5 rounded-sm">
                  <p className="font-mono text-xs text-[#C0A062] uppercase tracking-wider mb-1">
                    Custom Part Requests
                  </p>
                  <p className="text-[#A1A1AA] text-sm">
                    We can often reproduce parts even without an original sample
                    on hand.
                  </p>
                </div>
                <div className="p-4 bg-[#121212] border border-white/5 rounded-sm">
                  <p className="font-mono text-xs text-[#C0A062] uppercase tracking-wider mb-1">
                    Payment Options
                  </p>
                  <p className="text-[#A1A1AA] text-sm">
                    PayPal, Zelle, or Venmo available. Contact us to place an
                    order.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-[#121212] border border-white/5 rounded-sm p-8"
            >
              <ContactForm />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
